import { useState, useRef, useCallback, useEffect } from 'react';
import { STEP_MEDIA } from '../config/onboardingMedia';

/**
 * useAudioNarration - Hook for managing audio narration during onboarding
 *
 * Features:
 * - Play/pause/stop audio narration
 * - Track playback progress
 * - Auto-advance to next step on audio completion
 * - Volume control
 * - Persist audio preference
 */
export function useAudioNarration(locale = 'en') {
  const [isEnabled, setIsEnabled] = useState(() => {
    try {
      return localStorage.getItem('onboarding_audio_enabled') === 'true';
    } catch {
      return false;
    }
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => {
    try {
      return parseFloat(localStorage.getItem('onboarding_audio_volume') || '1');
    } catch {
      return 1;
    }
  });
  const [error, setError] = useState(null);

  const audioRef = useRef(null);
  const currentStepRef = useRef(null);

  // Save preferences
  useEffect(() => {
    try {
      localStorage.setItem('onboarding_audio_enabled', isEnabled.toString());
      localStorage.setItem('onboarding_audio_volume', volume.toString());
    } catch {
      // Ignore
    }
  }, [isEnabled, volume]);

  // Toggle audio enabled
  const toggleEnabled = useCallback(() => {
    setIsEnabled(prev => {
      const newValue = !prev;
      if (!newValue && audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
      return newValue;
    });
  }, []);

  // Load audio for a step
  const loadAudio = useCallback((stepId) => {
    // Stop current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setCurrentTime(0);
    setDuration(0);
    setError(null);
    currentStepRef.current = stepId;

    const media = STEP_MEDIA[stepId];
    const audioUrl = media?.audio?.[locale] || media?.audio?.en;

    if (!audioUrl) {
      return null;
    }

    const audio = new Audio(audioUrl);
    audio.volume = volume;

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
    });

    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
    });

    audio.addEventListener('ended', () => {
      setIsPlaying(false);
    });

    audio.addEventListener('error', (e) => {
      console.warn('Audio playback error:', e);
      setError('Audio not available');
      setIsPlaying(false);
    });

    audioRef.current = audio;
    return audio;
  }, [locale, volume]);

  // Play audio for a step
  const play = useCallback((stepId) => {
    if (!isEnabled) return;

    // If different step, load new audio
    if (stepId !== currentStepRef.current) {
      loadAudio(stepId);
    }

    if (audioRef.current) {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setError(null);
        })
        .catch((err) => {
          console.warn('Failed to play audio:', err);
          setError('Could not play audio');
          setIsPlaying(false);
        });
    }
  }, [isEnabled, loadAudio]);

  // Pause audio
  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  // Stop audio and reset
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  }, []);

  // Toggle play/pause
  const togglePlayback = useCallback((stepId) => {
    if (isPlaying) {
      pause();
    } else {
      play(stepId);
    }
  }, [isPlaying, pause, play]);

  // Seek to position
  const seek = useCallback((time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  // Change volume
  const changeVolume = useCallback((newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Get progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Check if audio is available for a step
  const hasAudio = useCallback((stepId) => {
    const media = STEP_MEDIA[stepId];
    return !!(media?.audio?.[locale] || media?.audio?.en);
  }, [locale]);

  return {
    // State
    isEnabled,
    isPlaying,
    currentTime,
    duration,
    volume,
    progress,
    error,

    // Actions
    toggleEnabled,
    play,
    pause,
    stop,
    togglePlayback,
    seek,
    changeVolume,
    loadAudio,
    hasAudio,
  };
}

/**
 * AudioControls - Component for audio playback controls
 */
export const AudioControls = ({
  isEnabled,
  isPlaying,
  progress,
  volume,
  hasAudio,
  onToggleEnabled,
  onTogglePlayback,
  onVolumeChange,
  onSeek,
  error,
}) => {
  const [showVolume, setShowVolume] = useState(false);

  if (!hasAudio) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {/* Enable/disable toggle */}
      <button
        onClick={onToggleEnabled}
        className={`p-2 rounded-lg transition-colors ${
          isEnabled
            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
            : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
        }`}
        title={isEnabled ? 'Disable audio narration' : 'Enable audio narration'}
      >
        {isEnabled ? (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
          </svg>
        )}
      </button>

      {/* Play/pause button (only when enabled) */}
      {isEnabled && (
        <>
          <button
            onClick={onTogglePlayback}
            className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          {/* Progress bar */}
          <div className="flex-1 h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden min-w-[60px]">
            <div
              className="h-full bg-blue-500 transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Volume control */}
          <div className="relative">
            <button
              onClick={() => setShowVolume(!showVolume)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6v12m0-12L8 10H5a1 1 0 00-1 1v2a1 1 0 001 1h3l4 4" />
              </svg>
            </button>

            {showVolume && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                  className="w-20 h-2 accent-blue-600"
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* Error indicator */}
      {error && (
        <span className="text-xs text-red-500 dark:text-red-400">{error}</span>
      )}
    </div>
  );
};

export default useAudioNarration;
